// Deterministic content hash of an album's PDF-relevant source.
//
// Multi-album orders (Duo/Trio) historically re-rendered EVERY album's PDFs
// whenever any one album was confirmed/regenerated — wasteful, since editing
// album 1 leaves albums 2 and 3 untouched. We now fingerprint each album's
// render-relevant inputs and store the fingerprint next to its generated PDFs
// (`album_sessions.pdf_source_hash`). A regenerate recomputes the fingerprint
// and SKIPS rendering any album whose hash still matches — only edited albums
// are reprocessed.
//
// The hash MUST be identical whether computed from live editor state (at submit
// time) or from a session row reloaded from the DB, so it only consumes fields
// that round-trip through `album_sessions`:
//   - pages          : { pages, unassigned_photos, intro_elements } JSONB
//                      (includes embedded slot_positions → captures every
//                       pan/zoom/crop and layout change)
//   - cover_id       : which cover design
//   - cover_destination_text / cover_year_text
//   - cover_customizations : spine, text-zone overrides, back-cover, etc.
//
// NOT captured: in-place photo edits that don't change the session JSON (e.g. a
// rotation that mutates only photos.width/height/face_data). Those are rare and
// an admin can still force a full regen. Slot/photo *replacements* DO change the
// pages JSON and are caught.

export interface AlbumSourceHashInput {
  pages: unknown;
  cover_id: string | null | undefined;
  cover_destination_text: string | null | undefined;
  cover_year_text: string | null | undefined;
  cover_customizations: unknown;
  /** B5 — present only when a gap is on; see multiAlbumPdf.photoGapHashInput. */
  photo_gap?: { mm: number; color: string } | null;
  /** COUPONS (2026-10-28) — what this album's coupon leaf prints (couponPrint.
   *  couponHashInput). Present ONLY when coupons print inside THIS album, so
   *  with the switch off every hash is exactly what it was and nothing
   *  re-renders. An INTERIOR input: the cover never sees it. */
  coupons?: unknown;
  /** « Photo Vivante » (livingPrint.livingHashInput): the still is the book's
   *  last page AND the QR is on the back cover, so it belongs to BOTH halves.
   *  Absent → every hash exactly as before. */
  living?: unknown;
}

// Keys that change on every save without affecting the rendered output. Stripped
// before hashing so an otherwise-identical album doesn't look "edited".
const VOLATILE_KEYS = new Set(['validated_at']);

// Recursively rebuild `value` with object keys sorted and volatile keys dropped,
// so JSON.stringify produces a stable, order-independent string.
export function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) {
      if (VOLATILE_KEYS.has(key)) continue;
      out[key] = canonical(src[key]);
    }
    return out;
  }
  return value;
}

// FNV-1a (32-bit). Not cryptographic — we only need cheap, deterministic
// change-detection. Salting the result with the input length makes accidental
// collisions on large layouts vanishingly unlikely.
export function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

// Bump when the RENDERER changes output for identical source (layout math,
// raster fixes, bleed, fonts…). The version is hashed with the source, so a
// renderer fix invalidates every stored pdf_source_hash and the next regen
// re-renders instead of skipping stale-but-"unchanged" albums.
//   v2 (2026-06-12): text-element raster honours editor overflow — text taller
//   than its box grows the raster instead of clipping to the middle slice.
//   v3 (2026-06-28): text wrap parity — over-wide no-space words are now
//   char-broken (CSS word-break: break-word) instead of overflowing one
//   clipped line, and fonts are awaited before the raster measures so wrap
//   points match the editor. Existing albums must re-render to pick this up.
//   v4 (2026-06-28): EXIF orientation parity — un-baked sideways/mirrored
//   photos (EXIF orientation 2..8) are now baked UPRIGHT on a canvas before
//   embedding (read via exifr, not the unreliable headless
//   createImageBitmap 'from-image'), so the PDF matches the editor instead of
//   showing a mis-oriented crop / cream gap (#1083). Upright photos (orient 1/
//   absent) embed their original bytes as-is. Every old album must re-render
//   to pick this up — else the unchanged source hash skips the fix.
// v5 (2026-07-01): interior finished trim corrected A4 210mm → 200mm wide
// (matches the 200mm board). 210-wide interiors were over-trimmed ~5mm/side,
// cutting faces at the edges. Every album must re-render to pick up 200×297.
// v6 (2026-07-01): blank-cover photo pan/zoom (blankCoverTransform) mirrored
// in the cover PDF + icon page elements (PageElement.imageUrl) rendered in the
// interior. Albums using either must re-render.
// v8 (2026-07-17): white-matte PNG encoder — transparent pixels forced white
// (v7 matte was a no-op due to canvas premultiplied alpha).
// v9 (2026-07-23): photo-element CROP (PageElement.crop) rendered in the PDF.
// v10 (2026-07-24): SLOT crop (SlotPosition.crop) — real cut, cover-fit on cropped dims + oversize/offset draw.
// v12 (2026-08-20): a blank ("vierge") cover draws its text zones. `cover_id:
// null` removes the artwork AND used to remove the title with it, so a customer
// who put their own photo on the front lost the words on it. The renderer now
// supplies the picker's own blank-cover zones (blankCoverZones.ts) — identical
// source, different output, which is exactly what this version salt is for.
// Without the bump every existing blank-cover album keeps its missing title
// forever, because its stored hash still matches.
// v13 (2026-09-26, #1215): a placed photo that cannot be drawn STOPS the render
// (it used to be painted cream — pages 2, 5 and 34 of a 56-page book came out
// empty), every page is counted against the editor, and the print copy is the
// original only. PDFs from v12 may hold silent holes; none of them may print.
// v14 (2026-10-06, #WA-269649): text elements with Arabic letters — each
// paragraph is laid out in its own direction (an Arabic line's full stop on
// its LEFT, as the editor's `unicode-bidi: plaintext` shows it), Arabic letters
// are drawn in a NAMED web font (Cairo / Amiri, loaded for that text) instead
// of the rendering machine's system fallback, one trailing newline is no
// longer an extra line, and caption outlines are drawn. Same source, different
// print: every album with Arabic text must re-render.
// live v18 (10-14): #RRGGBBAA fills keep their colour (printed black). v20 = that + design system v2.
// v18 (2026-10-14, design system v2 §7 S1): a THEMED book (first page the S1
// opening, bookStructure.ts) prints no intro page and its base-rules frames
// take the printed book's spine side (album page 1 on the right). Same pages,
// different interior; a plain book renders exactly as v17.
// v22 (10-15): script-font descenders no longer cut in the PDF (v2-check-b a00f6a7).
export const PDF_RENDER_VERSION = 31; // 31 cover TEXT elements (words on the front, back, spine: font, size, box, line spacing, band + margin — owner 10-30) · 30 Arabic cover title, spine and extra texts drawn by the browser (joined, RTL, named Arabic face — #WA-576804, 10-26) · 29 cover order stamp back under the spine, centred in the bottom turn-in (owner 10-23) · 28 order stamp « #1242 1/1 »: back board, 12 mm inside, pure black/white from the real pixels, boxed when busy, bold 8 pt — cover + interior first page (owner 10-21) · 27 cover elements: stickers / icons / the client's photos on the front, back and spine (cover_customizations.coverElements, 10-20) · 26 editor text printed as vector outlines, no transparent picture (#1236, 10-17) · 25 cover titles: the editor's baseline + size (#1248, 10-17) · 24 themed books print the intro, page 1 on the left (owner 10-14) · 23 tinted pieces (4-10 Collage anniversaire: the admin's colour) · 15 wrap keeps spaces (#1236) · 16 trim-slot placement + CSS baseline · 17 printer upgrade (step 2) + themes (step 3) · 18 themed book structure (S1 opens, no intro) · 19 a paper as background + as a layer = one embed

export function albumSourceHash(input: AlbumSourceHashInput): string {
  const payload = canonical({
    v: PDF_RENDER_VERSION,
    pages: input.pages ?? null,
    cover_id: input.cover_id ?? null,
    dest: input.cover_destination_text ?? null,
    year: input.cover_year_text ?? null,
    cust: input.cover_customizations ?? null,
    ...(input.photo_gap ? { gap: input.photo_gap } : {}),
    ...(input.coupons ? { coupons: input.coupons } : {}),
    ...(input.living ? { living: input.living } : {}),
  });
  const json = JSON.stringify(payload);
  return `${fnv1a(json)}-${json.length.toString(16)}`;
}

// ─── Per-part hashes: a cover edit must not re-render the interior ───────────
//
// 2026-08-20. Changing a cover title, colour or photo from WhatsApp sends the
// album back to the renderer, and the renderer rebuilt EVERYTHING — 24 pages of
// full-resolution photos to reprint one word on the front. The owner, watching
// a black cover take minutes to appear: "if we did changes on the cover only
// edit it and regenerate only the PDF cover so it can be fast."
//
// The inputs already split cleanly, which is what makes this safe:
//
//     interior  ←  pages          (layouts, slot positions, crops, captions)
//     cover     ←  cover_id, cover_destination_text, cover_year_text,
//                  cover_customizations
//
// ⚠️ NOTHING IS SHARED BETWEEN THEM. `pages` never reaches generateCoverPdf and
// no `cover_*` field reaches generateInteriorPdf — verified against both
// generators before this was written. If that ever stops being true, a change
// to the shared field would skip the half that needed it and print a stale
// book, so a new input belongs in BOTH hashes until proven otherwise.
//
// ⚠️ PDF_RENDER_VERSION IS IN BOTH. A renderer fix has to invalidate both
// halves; salting only one would leave the other frozen at the old output
// forever, which is the exact failure the version salt exists to prevent.

/** The interior's inputs. `pages` carries slot positions, crops and captions. */
export function interiorSourceHash(input: AlbumSourceHashInput): string {
  const json = JSON.stringify(canonical({
    v: PDF_RENDER_VERSION,
    pages: input.pages ?? null,
    // B5 — the gap is an interior input: same pages, different book.
    ...(input.photo_gap ? { gap: input.photo_gap } : {}),
    // Coupon leaf (2026-10-28): only when this album prints one.
    ...(input.coupons ? { coupons: input.coupons } : {}),
    ...(input.living ? { living: input.living } : {}),
  }));
  return `${fnv1a(json)}-${json.length.toString(16)}`;
}

/** The cover's inputs: which design, the printed words, and every override. */
export function coverSourceHash(input: AlbumSourceHashInput): string {
  const json = JSON.stringify(canonical({
    v: PDF_RENDER_VERSION,
    cover_id: input.cover_id ?? null,
    dest: input.cover_destination_text ?? null,
    year: input.cover_year_text ?? null,
    cust: input.cover_customizations ?? null,
    ...(input.living ? { living: input.living } : {}),
  }));
  return `${fnv1a(json)}-${json.length.toString(16)}`;
}

/**
 * What gets stored in `album_sessions.pdf_source_hash`.
 *
 * ⚠️ ONE FORMAT EVERYWHERE. The editor and the render probe both compare this
 * string against the stored one; two formats would mean one of them never
 * matches and silently re-renders every time. An OLD single-hash value simply
 * fails to parse below and costs one full render, once — which errs toward
 * rendering, never toward skipping.
 */
export function compositeSourceHash(input: AlbumSourceHashInput): string {
  return `i${interiorSourceHash(input)}${PART_SEP}c${coverSourceHash(input)}`;
}

/**
 * ⚠️ A SEPARATOR THE DIGESTS CANNOT CONTAIN.
 *
 * The first version of this used `c` as the marker — `i<hex>c<hex>` — and the
 * digests are HEX, so `indexOf('c')` landed inside the interior hash and split
 * it in the wrong place. `i1f5c1d84-8a3ce0bcbcf4-1e1` parsed as interior `1f5`
 * and cover `1d84-…`. It failed safe (neither half ever matched, so both
 * re-rendered) which is why nothing printed wrong — but the whole optimisation
 * was silently doing nothing. A digest is `[0-9a-f]` plus `-`; a pipe cannot
 * appear in one.
 */
const PART_SEP = '|';

export interface SourceHashParts {
  interior: string;
  cover: string;
}

/**
 * Split a stored composite back into its two halves.
 *
 * Returns null for anything else — an old single hash, an empty column, a
 * truncated value. ⚠️ NULL MUST MEAN "RENDER BOTH". The only unrecoverable
 * mistake here is skipping a part that needed rendering, so every uncertainty
 * resolves toward doing the work.
 */
export function parseSourceHash(stored: string | null | undefined): SourceHashParts | null {
  const s = String(stored ?? '');
  const [left, right, ...rest] = s.split(PART_SEP);
  if (rest.length) return null;                       // more than two halves
  if (!left?.startsWith('i') || !right?.startsWith('c')) return null;
  const interior = left.slice(1);
  const cover = right.slice(1);
  if (!interior || !cover) return null;
  return { interior, cover };
}

/** Which halves have to be re-rendered. */
export interface RenderParts {
  cover: boolean;
  interior: boolean;
}

/**
 * Decide what to render for an album.
 *
 * ⚠️ EVERY DOUBT RENDERS. A missing PDF, an unparseable stored hash, `force` —
 * all of them mean "do the work". The cost of rendering something that did not
 * change is a minute of compute; the cost of skipping something that did is a
 * printed book with the wrong cover on it.
 */
export function partsToRender(opts: {
  stored: string | null | undefined;
  current: AlbumSourceHashInput;
  hasCoverPdf: boolean;
  hasInteriorPdf: boolean;
  force?: boolean;
}): RenderParts {
  if (opts.force) return { cover: true, interior: true };
  const prev = parseSourceHash(opts.stored);
  if (!prev) return { cover: true, interior: true };
  return {
    cover: !opts.hasCoverPdf || prev.cover !== coverSourceHash(opts.current),
    interior: !opts.hasInteriorPdf || prev.interior !== interiorSourceHash(opts.current),
  };
}
